import { Loader } from '../modules/Loader';
import {
    addInspirationAllToCart,
    exportInspirationShoppingListPdf,
    openInspirationSidebar,
} from '../modules/inspirations/inspiration-api';

class Inspirations {
    constructor(section = null) {
        if (!section) return;
        this.root = section;
        this.activeInspirationTermId = 0;
        this.inspirationSidebar = null;
        this.loader = new Loader();

        this.init();
    }

    init() {
        this.galleryItems = this.root.querySelectorAll('[js-gallery-item]');
        this.radios = this.root.querySelectorAll('input[name="inspiration"]');
        this.paginationElement = this.root.querySelector('[js-pagination]');
        this.currentNumberElement =
            this.paginationElement?.querySelector('[js-current]');
        this.totalNumberElement =
            this.paginationElement?.querySelector('[js-total]');
        this.loadMoreButton = this.paginationElement?.querySelector(
            '.js-inspirations-pagination-button'
        );
        this.progressBar = this.root.querySelector('[js-progress-bar]');

        this.activeElements = Array.from(this.galleryItems).filter(
            item => item.getAttribute('data-active') == 1
        );
        this.totalItems = this.galleryItems.length;
        this.itemsPerPage = window.innerWidth > 1023.98 ? 5 : 3;
        this.isDesktopView = window.innerWidth > 1023.98;
        this.activeTab = 'all';

        this.handleRootClick = this.onRootClick.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);

        this.events();
    }

    events() {
        if (this.radios.length) {
            this.radios.forEach(element => {
                element.addEventListener(
                    'change',
                    this.onRadioChange.bind(this)
                );
            });
        }

        if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener(
                'click',
                this.onLoadMoreButtonClick.bind(this)
            );
        }

        this.root.addEventListener('click', this.handleRootClick);
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('resize', this.onWindowResize.bind(this));
    }

    onRootClick(event) {
        const inspirationOpenTrigger = event.target.closest(
            '[data-inspiration-open]'
        );

        if (inspirationOpenTrigger) {
            event.preventDefault();
            event.stopPropagation();
            this.openSidebarFromCard(inspirationOpenTrigger);
            return;
        }

        const card = event.target.closest('[js-inspiration-card]');

        if (card && this.root.contains(card)) {
            event.preventDefault();
            this.openSidebarFromCard(card);
        }
    }

    openSidebarFromCard(trigger) {
        const card = trigger.closest('[js-inspiration-card]');
        const termId = Number(card?.dataset?.termId || 0);

        if (termId <= 0) {
            return;
        }

        void this.openInspirationSidebar(termId);
    }

    async openInspirationSidebar(termId) {
        this.loader?.show();

        try {
            await openInspirationSidebar(this, termId);
        } finally {
            this.loader?.hide();
        }
    }

    onDocumentClick(event) {
        if (!this.inspirationSidebar?.content?.contains(event.target)) {
            return;
        }

        const exportPdfButton = event.target.closest(
            '[data-inspiration-export-pdf]'
        );

        if (exportPdfButton) {
            event.preventDefault();
            event.stopPropagation();

            const termId = Number(
                exportPdfButton.dataset.termId ||
                    this.activeInspirationTermId ||
                    0
            );

            if (termId > 0) {
                void exportInspirationShoppingListPdf(this, termId);
            }

            return;
        }

        const addAllButton = event.target.closest(
            '[data-inspiration-add-all-to-cart]'
        );

        if (addAllButton) {
            event.preventDefault();
            event.stopPropagation();

            if (
                addAllButton.disabled ||
                addAllButton.getAttribute('aria-disabled') === 'true'
            ) {
                return;
            }

            const termId = Number(
                addAllButton.dataset.termId || this.activeInspirationTermId || 0
            );

            if (termId > 0) {
                void addInspirationAllToCart(this, termId);
            }
        }
    }

    onRadioChange(e) {
        this.activeTab = e.target.value;

        this.galleryItems.forEach(item => {
            item.classList.add('hidden');
            item.setAttribute('data-active', 0);
        });

        if (this.activeTab === 'all') {
            this.galleryItems = this.root.querySelectorAll('[js-gallery-item]');
        } else {
            const [taxonomy, termId] = String(this.activeTab).split(':');
            const attributeName =
                taxonomy === 'room' ? 'data-room-ids' : 'data-style-ids';

            this.galleryItems = Array.from(
                this.root.querySelectorAll('[js-gallery-item]')
            ).filter(item => {
                const ids = String(item.getAttribute(attributeName) || '')
                    .split(',')
                    .filter(Boolean);

                return ids.includes(String(termId));
            });
        }

        this.galleryItems.forEach((item, key) => {
            this.prepareItemClasses(item, key);
        });

        this.totalItems = this.galleryItems.length;
        this.paginationReset();
    }

    prepareItemClasses(item, key) {
        const group = key % 5;
        const media = item.querySelector('.hover-scale-image__media');
        const image = media?.querySelector('img') || item.querySelector('img');

        if (!image) {
            return;
        }

        item.classList.remove(...item.classList);
        image.classList.remove(...image.classList);
        item.classList.add(
            'col-span-2',
            'group',
            'relative',
            'cursor-pointer',
            'hover-scale-image-on-group'
        );
        image.classList.add('w-full', 'object-cover', 'rounded-small');

        switch (group) {
            case 0:
                image.classList.add('desktop:aspect-[5/4]');
                item.classList.add('desktop:col-span-7');
                break;
            case 1:
                image.classList.add('desktop:aspect-[16/9]');
                item.classList.add('desktop:col-span-5');
                break;
            case 2:
                image.classList.add('desktop:aspect-[4/5]');
                item.classList.add('desktop:col-span-4');
                break;
            case 3:
                image.classList.add('desktop:aspect-[5/4]');
                item.classList.add('desktop:col-span-3');
                break;
            case 4:
                image.classList.add('desktop:aspect-[4/5]');
                item.classList.add('desktop:col-span-5');
                break;
        }
    }

    onLoadMoreButtonClick() {
        let possibleActiveElements = this.itemsPerPage;
        this.galleryItems.forEach(element => {
            if (
                possibleActiveElements !== 0 &&
                element.getAttribute('data-active') != '1'
            ) {
                element.setAttribute('data-active', '1');
                element.classList.remove('hidden');
                possibleActiveElements = possibleActiveElements - 1;
            }
        });

        this.activeElements = Array.from(this.galleryItems).filter(
            item => item.getAttribute('data-active') == 1
        );

        this.handlePaginationVisbility();
    }

    handlePaginationVisbility() {
        if (!this.currentNumberElement || !this.totalNumberElement) {
            return;
        }

        this.currentNumberElement.textContent = this.activeElements.length;
        this.totalNumberElement.textContent = this.totalItems;

        if (this.progressBar) {
            this.progressBar.style.width = `${
                (this.activeElements.length / this.totalItems) * 100
            }%`;
        }

        if (!this.loadMoreButton) {
            return;
        }

        if (this.activeElements.length === this.totalItems) {
            this.loadMoreButton.classList.add('hidden!');
        } else {
            this.loadMoreButton.classList.remove('hidden!');
        }
    }

    onWindowResize() {
        const width = window.innerWidth;

        if (width > 1023.98 && !this.isDesktopView) {
            this.itemsPerPage = 5;
            this.isDesktopView = true;
        } else if (width <= 1023.98 && this.isDesktopView) {
            this.itemsPerPage = 3;
            this.isDesktopView = false;
        } else {
            return;
        }

        this.paginationReset();
    }

    paginationReset() {
        let possibleActiveElements = this.itemsPerPage;
        this.galleryItems.forEach((element, key) => {
            if (key + 1 > possibleActiveElements) {
                element.setAttribute('data-active', '0');
                element.classList.add('hidden');
            } else {
                element.setAttribute('data-active', '1');
                element.classList.remove('hidden');
            }
        });

        this.activeElements = Array.from(this.galleryItems).filter(
            item => item.getAttribute('data-active') == 1
        );

        this.handlePaginationVisbility();
    }
}

const sections = document.querySelectorAll('section.inspirations');

if (sections.length) {
    sections.forEach(section => {
        new Inspirations(section);
    });
}
